import {
	Component,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	SimpleChanges,
	ViewChild
} from '@angular/core';

import type { ColDef, GridOptions } from 'ag-grid-community';

import { AnalyticsService } from 'rev-shared/analytics/Analytics.Service';
import { IVbUiDataGridColDef } from 'rev-shared/ui/dataGrid/columns/IVbUiDataGridColDef';
import { IWebcastViewContext } from 'rev-shared/webcast/webcastView/IWebcastViewContext';
import { SecondMs } from 'rev-shared/date/Time.Constant';
import { VbUiInfiniteScrollGridComponent } from 'rev-shared/ui/dataGrid/infiniteScroll/VbUiInfiniteScrollGrid.Component';

import { AttendeesListService } from './AttendeesList.Service';
import { WebcastModel } from '../model/WebcastModel';
import { WebcastAttendeeRendererComponent } from './WebcastAttendeeRenderer.Component';

import './webcast-attendees-list.less';

@Component({
	selector: 'webcast-attendees-list',
	templateUrl: './WebcastAttendeesList.Component.html',
	standalone: false
})
export class WebcastAttendeesListComponent implements OnInit, OnChanges, OnDestroy {
	@Input() public accountId: string;
	@Input() public countPollingIntervalInSec: number;
	@Input() public listPollingIntervalInSec: number;
	@Input() public webcast: WebcastModel;
	@Input() public context: IWebcastViewContext;
	@Input() public isPollingActive: boolean;

	public readonly WebcastAttendeeRendererComponent = WebcastAttendeeRendererComponent;
	public readonly pageSize = 50;

	public attendeeCount: number;
	@ViewChild(VbUiInfiniteScrollGridComponent)
	public grid: VbUiInfiniteScrollGridComponent;
	public searchInput: string;
	public isGridReady: boolean;
	public gridColumnDefs: IVbUiDataGridColDef[];
	public gridContext: any = {};
	public gridDefaultColumnDef: ColDef = {};
	public gridOptions: GridOptions = {};

	private attendeeCountIntervalRef: number;

	constructor(
		public AnalyticsService: AnalyticsService,
		public AttendeeService: AttendeesListService
	) {}

	public ngOnInit(): void {
		this.assignGridSettings();
		this.countPollingIntervalInSec = this.countPollingIntervalInSec || this.AnalyticsService.kpiUpdateIntervalInSecs;
		this.listPollingIntervalInSec = this.listPollingIntervalInSec || this.AnalyticsService.tableUpdateIntervalInSecs;

		this.AttendeeService.initAttendeesView(this.webcast.id, this.webcast.currentRun.runNumber);
	}

	public ngOnChanges(changes: SimpleChanges): void {
		if (changes.isPollingActive) {
			this.handlePolling();
		}
	}

	public ngOnDestroy(): void {
		if (this.attendeeCountIntervalRef) {
			window.clearInterval(this.attendeeCountIntervalRef);
			this.attendeeCountIntervalRef = undefined;
		}
	}

	public onGridReady(): void {
		this.isGridReady = true;
		this.handlePolling();
	}

	private handleRemoveAndRestoreAttendee(isRestore: boolean, attendee: any): void {
		this.grid.pausePolling();

		(isRestore ?
			this.AttendeeService.restoreAttendee(this.webcast.id, this.webcast.currentRun.runNumber, attendee) :
			this.AttendeeService.removeAttendee(this.webcast.id, this.webcast.currentRun.runNumber, attendee)
		)
			.then(() => attendee.Removed = !isRestore)
			.finally(() => this.resumeAttendeeListPolling());
	}

	private handlePolling(): void {
		if(this.grid && this.grid.isPollingActive !== this.isPollingActive) {
			if (this.isPollingActive) {
				this.grid.resumePolling();
			}
			else {
				this.grid.pausePolling();
			}
		}
	}

	private resumeAttendeeListPolling(): void {
		setTimeout(() => this.grid.resumePolling(), this.listPollingIntervalInSec * SecondMs);
	}

	private assignGridSettings(): void {
		this.assignGridDefaultColDef();
		this.assignGridOptions();
		this.assignGridContext();
		this.assignGridColumnDefs();
	}

	private assignGridContext(): void {
		this.gridContext = {
			webcast: this.webcast,
			restoreAttendee: attendee => this.handleRemoveAndRestoreAttendee(true, attendee),
			removeAttendee: attendee => this.handleRemoveAndRestoreAttendee(false, attendee)
		};
	}

	private assignGridDefaultColDef(): void {
		this.gridDefaultColumnDef = {
			cellRenderer: WebcastAttendeeRendererComponent
		};
	}

	private assignGridOptions(): void {
		this.gridOptions = {
			rowHeight: 55,
			headerHeight: 0,
			suppressMultiSort: true
		};
	}

	private assignGridColumnDefs(): void {
		this.gridColumnDefs = [
			{
				filter: true,
				field: 'FullName'
			}
		];
	}
}
